import React from 'react'
import {Box, Typography} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import dayjs, {Dayjs} from 'dayjs'
import Card from 'src/components-new/layout/Card'
import ChartCard from 'src/components-new/data/ChartCard'
import EmptyState from 'src/components-new/layout/EmptyState'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {
  ADHERENCE_DEFAULT_RANGE_DAYS,
  AdherenceFilters,
  AdherenceLabels,
  AdherenceResponse,
  AdherenceSection,
  buildAdherenceByTherapyOptions,
  buildAdherenceTimelineOptions,
  formatAdherencePercentage,
} from 'src/utils/adherenceCharts'

// Same window the backend falls back to, made explicit so the "Data" filter shows it pre-filled
// and "Azzera" brings it back instead of clearing to an unbounded range.
export function defaultAdherenceRange(): [Dayjs, Dayjs] {
  return [dayjs().subtract(ADHERENCE_DEFAULT_RANGE_DAYS, 'day'), dayjs()]
}

export function defaultAdherenceFilters(): AdherenceFilters {
  const [from, to] = defaultAdherenceRange()
  return {dateFrom: from.startOf('day').toISOString(), dateTo: to.endOf('day').toISOString()}
}

export function formatAdherenceDate(iso: string): string {
  return dayjs(iso).format('DD/MM/YYYY')
}

function TotalsCard({section, labels}: {section: AdherenceSection; labels: AdherenceLabels}): JSX.Element {
  const items = [
    {label: labels.adherence, value: formatAdherencePercentage(section.percentage)},
    {label: labels.completedAssumptions, value: section.completed},
    {label: labels.expectedAssumptions, value: section.total},
  ]

  return (
    <Card title={labels.totalTitle}>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 2}}>
        {items.map(item => (
          <Box key={item.label} sx={{border: `1px solid ${NEUTRAL_SCALE[200]}`, borderRadius: 1, px: 2, py: 1.5}}>
            <Typography sx={{fontSize: 12, color: 'text.secondary', textTransform: 'uppercase'}}>
              {item.label}
            </Typography>
            <Typography sx={{fontSize: 24, fontWeight: 600}}>{item.value}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  )
}

// Totals, timeline and per-therapy split of one adherence section. Shared by the patient's own
// Analytics page and the "Aderenza terapia" tab of the doctor's patient detail, so the two always
// draw the same numbers the same way.
export default function AdherenceSectionCharts({section, granularity, labels}: {
  section: AdherenceSection
  granularity: AdherenceResponse['granularity']
  labels: AdherenceLabels
}): JSX.Element {
  const theme = useTheme()
  const color = theme.palette.primary.main

  if (section.total === 0) {
    return (
      <EmptyState
        title="Nessuna assunzione prevista"
        description="Non ci sono assunzioni previste per i filtri selezionati. Modifica i filtri per vedere l'aderenza."
      />
    )
  }

  return (
    <Box sx={{display: 'grid', gap: 2}}>
      <TotalsCard section={section} labels={labels} />
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2}}>
        <ChartCard title={labels.timelineTitle} options={buildAdherenceTimelineOptions(section, granularity, color)} />
        <ChartCard title={labels.byTherapyTitle} options={buildAdherenceByTherapyOptions(section, color)} />
      </Box>
    </Box>
  )
}
